import { getFluidPropertiesContext } from "@/libs/calculations/fluidProperties/getFluidPropertiesContext";
import { Tubing_Flow_Options_Enum } from "gql/api";
import {
  getDeviationSurveyPointsBetweenMeasuredDepths,
  getHydrostaticSumForPiping
} from "@/libs/calculations/deviationSurvey";
import { wellFlowingTemperatureForMD } from "@/libs/calculations/pressureDropCorrelations";
import type { PipeFrictionProperties } from "@/libs/calculations/tubingFrictionLoss";
import { computeRequiredDischargePressure } from "@/libs/calculations/tubingFrictionLoss";
import {
  calculateTopTubingFrictionProperties,
  calculateBottomTubingFrictionProperties
} from "../../tubingFrictionCalculations";
import type { FluidPropertiesContext } from "@/libs/calculations/fluidProperties/d";
import type { FluidPropertiesPipelineInputs } from "../types";

// Tubing friction, required discharge pressure, and fluid properties after
// the pump discharge.
export const calculateTubingAndDischarge = (
  inputs: FluidPropertiesPipelineInputs,
  prev: {
    wellFlowingPressureAtPumpPSIA: number;
    fluidPropertiesAfterAllGasSeparationAtPumpContext: FluidPropertiesContext;
  }
) => {
  const {
    basicData,
    correlations,
    desiredFlowRates,
    bubblePointPressurePSIA,
    condensateApi,
    dewPointPressurePSIA,
    entryRows,
    customFluidProperties,
    customViscosityProperties,
    fluidPropertiesOverrides,
    tdhTuningFactor,
    topOfPumpsTVDFt,
    bottomOfPumpsTVDFt,
    tubingInternalDiameterAtPumpDischargeIn
  } = inputs;
  const {
    wellFlowingPressureAtPumpPSIA,
    fluidPropertiesAfterAllGasSeparationAtPumpContext
  } = prev;

  const downholeTubingTemperatureF = wellFlowingTemperatureForMD(
    basicData.totalTubingLengthFt,
    basicData.datumPointFt,
    basicData.bottomholeTemperatureF,
    basicData.surfaceTemperatureF
  );

  const fluidPropertiesInsideTubingContext = getFluidPropertiesContext(
    basicData,
    correlations,
    wellFlowingPressureAtPumpPSIA,
    downholeTubingTemperatureF,
    tubingInternalDiameterAtPumpDischargeIn,
    desiredFlowRates,
    fluidPropertiesAfterAllGasSeparationAtPumpContext.flowRatesInSitu,
    fluidPropertiesAfterAllGasSeparationAtPumpContext.flowRatePrerequisites,
    customFluidProperties,
    customViscosityProperties,
    bubblePointPressurePSIA,
    dewPointPressurePSIA,
    0,
    0,
    fluidPropertiesOverrides,
    condensateApi,
    true
  );

  // Normalize tubing correlations needed for tubing friction calculations
  const resolvedTubingFlow =
    correlations?.tubing_flow ?? Tubing_Flow_Options_Enum.BeggsBrill;
  const resolvedTubingRoughnessInches =
    correlations?.tubing_roughness_inches ?? 0;

  const emptyTubingFrictionProperties: PipeFrictionProperties = {
    calcMethod: Tubing_Flow_Options_Enum.BeggsBrill,
    frictionPressurePSIA: 0,
    hydrostaticPressurePSIA: 0,
    intermediates: { frictionPressurePSIA: 0 }
  };

  // Total pipe length
  const pipeTotalLengthFt =
    basicData.topTubingLengthFt + basicData.bottomTubingLengthFt;
  const tubingRowData = getDeviationSurveyPointsBetweenMeasuredDepths(
    entryRows,
    0,
    pipeTotalLengthFt
  );

  // Top tubing friction properties
  let topTubingFrictionProperties: PipeFrictionProperties =
    emptyTubingFrictionProperties;
  if (basicData.topTubingLengthFt > 0) {
    topTubingFrictionProperties = calculateTopTubingFrictionProperties({
      correlations,
      topTubingInnerDiameterInches: basicData.topTubingInteriorDiameterIn,
      topTubingRoughnessInches: resolvedTubingRoughnessInches,
      topTubingLengthFt: basicData.topTubingLengthFt,
      bottomTubingInnerDiameterInches: basicData.bottomTubingInteriorDiameterIn,
      bottomTubingRoughnessInches: resolvedTubingRoughnessInches,
      bottomTubingLengthFt: basicData.bottomTubingLengthFt,
      topOfPumpsTVDFt,
      bottomOfPumpsTVDFt,
      topTubingDeviationSurveyEntryRows: entryRows,
      fluidPropertiesInsideTubingContext,
      surfaceTemperatureF: basicData.surfaceTemperatureF,
      bottomholeTemperatureF: basicData.bottomholeTemperatureF
    });
  }

  // Bottom tubing friction properties
  let frictionProperties: PipeFrictionProperties = topTubingFrictionProperties;
  let tubingFrictionLossPSIA = topTubingFrictionProperties.frictionPressurePSIA;
  let bottomTubingFrictionProperties: PipeFrictionProperties =
    emptyTubingFrictionProperties;
  if (basicData.bottomTubingLengthFt > 0) {
    bottomTubingFrictionProperties = calculateBottomTubingFrictionProperties({
      correlations,
      topTubingInnerDiameterInches: basicData.topTubingInteriorDiameterIn,
      topTubingRoughnessInches: resolvedTubingRoughnessInches,
      topTubingLengthFt: basicData.topTubingLengthFt,
      bottomTubingInnerDiameterInches: basicData.bottomTubingInteriorDiameterIn,
      bottomTubingRoughnessInches: resolvedTubingRoughnessInches,
      bottomTubingLengthFt: basicData.bottomTubingLengthFt,
      topOfPumpsTVDFt,
      bottomOfPumpsTVDFt,
      topTubingDeviationSurveyEntryRows: entryRows,
      fluidPropertiesInsideTubingContext,
      surfaceTemperatureF: basicData.surfaceTemperatureF,
      bottomholeTemperatureF: basicData.bottomholeTemperatureF
    });
    tubingFrictionLossPSIA +=
      bottomTubingFrictionProperties.frictionPressurePSIA;
    frictionProperties = bottomTubingFrictionProperties;
  }

  // Calculate requiredDischargePressure using the friction loss from above
  const hydrostaticSum = getHydrostaticSumForPiping(tubingRowData);
  const requiredDischargePressurePSIA = computeRequiredDischargePressure(
    resolvedTubingFlow,
    fluidPropertiesInsideTubingContext.fluidFrictionProperties.flowRegime,
    basicData.bottomTubingPressurePSIA,
    hydrostaticSum,
    tubingFrictionLossPSIA,
    frictionProperties
  );

  // Takes user input into account
  const adjustedRequiredDischargePressurePSIA =
    requiredDischargePressurePSIA * tdhTuningFactor;

  const fluidPropertiesAfterPumpDischargeTubingContext =
    getFluidPropertiesContext(
      basicData,
      correlations,
      adjustedRequiredDischargePressurePSIA,
      downholeTubingTemperatureF,
      tubingInternalDiameterAtPumpDischargeIn,
      desiredFlowRates,
      fluidPropertiesInsideTubingContext.flowRatesInSitu,
      fluidPropertiesInsideTubingContext.flowRatePrerequisites,
      customFluidProperties,
      customViscosityProperties,
      bubblePointPressurePSIA,
      dewPointPressurePSIA,
      0,
      0,
      fluidPropertiesOverrides,
      condensateApi,
      true
    );

  return {
    downholeTubingTemperatureF,
    fluidPropertiesInsideTubingContext,
    tubingFrictionLossPSIA,
    topTubingFrictionProperties,
    bottomTubingFrictionProperties,
    adjustedRequiredDischargePressurePSIA,
    fluidPropertiesAfterPumpDischargeTubingContext
  };
};
